// client/src/hooks/useCategoryLabels.js
// ================================================================
// REUSABLE HOOK: Fetches category labels from the registry API
// Returns a map of { key: "icon label" } for use in dropdowns/badges
// Auto-updates when language changes.
//
// Usage:
//   const { categoryLabelMap, getCategoryLabel, getUnitLabel } = useCategoryLabels();
//   // In JSX:  {getCategoryLabel(product.category)}
// ================================================================

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { registryAPI } from '../services/api';

const useCategoryLabels = () => {
  const { i18n } = useTranslation();
  const [categoryLabelMap, setCategoryLabelMap] = useState({});
  const [unitLabelMap, setUnitLabelMap] = useState({});
  const [unitShortMap, setUnitShortMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        setLoading(true);
        const lang = i18n?.language === 'el' ? 'el' : 'en';

        const [catRes, unitRes] = await Promise.all([
          registryAPI.getCategories(lang).catch(() => null),
          registryAPI.getUnits(lang, false).catch(() => null),
        ]);

        if (catRes?.data?.data) {
          const map = {};
          catRes.data.data.forEach(c => {
            map[c.key] = `${c.icon} ${c.label}`;
          });
          setCategoryLabelMap(map);
        }

        if (unitRes?.data?.data) {
          const full = {};
          const short = {};
          unitRes.data.data.forEach(u => {
            full[u.key] = u.label;
            // Short label: first word or abbreviation
            short[u.key] = u.label.replace(/\s*\(.*\)/, '').split(' ')[0];
          });
          setUnitLabelMap(full);
          setUnitShortMap(short);
        }
      } catch (err) {
        console.error('useCategoryLabels: failed to load registry', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLabels();
  }, [i18n?.language]);

  const getCategoryLabel = useCallback(
    (key) => categoryLabelMap[key] || key?.replace(/_/g, ' ') || '',
    [categoryLabelMap]
  );

  const getUnitLabel = useCallback(
    (key) => unitLabelMap[key] || key || '',
    [unitLabelMap]
  );

  const getUnitShort = useCallback(
    (key) => unitShortMap[key] || key || '',
    [unitShortMap]
  );

  return {
    categoryLabelMap,
    unitLabelMap,
    unitShortMap,
    getCategoryLabel,
    getUnitLabel,
    getUnitShort,
    loading,
  };
};

export default useCategoryLabels;